<template>
	<div class="cms-pricing-block">
		<div class="cms-pricing-block__card" :style="cardStyle">
			<!-- "Best Option" ribbon -->
			<div v-if="ribbonText" class="cms-pricing-block__ribbon" :style="ribbonStyle">
				{{ ribbonText }}
			</div>

			<!-- Icon -->
			<div v-if="icon" class="cms-pricing-block__icon-wrap" :style="iconWrapStyle">
				<i :class="'fa fa-' + icon" :style="iconStyle"></i>
			</div>

			<!-- Plan name -->
			<div v-if="planName" class="cms-pricing-block__plan" :style="planStyle">{{ planName }}</div>

			<!-- Tagline -->
			<div v-if="tagline" class="cms-pricing-block__tagline" :style="taglineStyle">{{ tagline }}</div>

			<!-- Divider -->
			<div class="cms-pricing-block__divider" :style="dividerStyle"></div>

			<!-- Price -->
			<div class="cms-pricing-block__price-wrap">
				<span v-if="originalPrice" class="cms-pricing-block__original-price" :style="originalPriceStyle">
					{{ currencySymbol }}{{ originalPrice }}
				</span>
				<span class="cms-pricing-block__price" :style="priceStyle">
					<span class="cms-pricing-block__currency">{{ currencySymbol }}</span>
					{{ price }}
				</span>
				<span v-if="pricePeriod" class="cms-pricing-block__period" :style="periodStyle">/ {{ pricePeriod }}</span>
			</div>

			<!-- Feature list -->
			<ul v-if="parsedFeatures.length" class="cms-pricing-block__features" :style="featuresStyle">
				<li
					v-for="(feature, idx) in parsedFeatures"
					:key="idx"
					class="cms-pricing-block__feature"
					:class="{ 'cms-pricing-block__feature--disabled': feature.disabled }"
					:style="featureStyle(feature)"
				>
					<i
						class="fa cms-pricing-block__feature-icon"
						:class="feature.disabled ? 'fa-times' : 'fa-check'"
						:style="featureIconStyle(feature)"
					></i>
					{{ feature.text }}
				</li>
			</ul>

			<!-- Divider before CTA -->
			<div class="cms-pricing-block__divider" :style="dividerStyle"></div>

			<!-- CTA Button -->
			<div class="cms-pricing-block__cta-wrap">
				<a
					:href="buttonUrl || '#'"
					class="cms-pricing-block__cta"
					:style="buttonStyle"
				>
					<i v-if="buttonIcon" :class="'fa fa-' + buttonIcon"></i>
					{{ buttonLabel || 'Get Started' }}
				</a>
			</div>

			<!-- Optional note below button -->
			<div v-if="footerNote" class="cms-pricing-block__note" :style="noteStyle">{{ footerNote }}</div>
		</div>
	</div>
</template>

<script>
/**
 * PricingBlock.vue
 * ────────────────
 * A complete pricing card with:
 *   - Optional "best option" ribbon
 *   - Icon (Font Awesome)
 *   - Plan name & tagline
 *   - Price with optional crossed-out original price
 *   - Feature list (with enabled/disabled states)
 *   - Call-to-action button
 *   - Fully configurable colours and styles
 *
 * The card's outer background, border, radius, shadow and padding are wrapper
 * traits (see the block's defaultStyle). The inner `__card` wrapper only
 * clips the ribbon and inherits the wrapper's radius.
 */
export default {
	name: 'PricingBlock',

	props: {
		/* ── Identity ── */
		planName: { type: String, default: 'Starter' },
		tagline:  { type: String, default: '' },
		icon:     { type: String, default: '' },

		/* ── Pricing ── */
		price:          { type: String, default: '29' },
		originalPrice:  { type: String, default: '' },
		currencySymbol: { type: String, default: '$' },
		pricePeriod:    { type: String, default: 'mo' },

		/* ── Features: JSON array of { text, disabled? } ── */
		features: { type: String, default: '[]' },

		/* ── CTA ── */
		buttonLabel: { type: String, default: 'Get Started' },
		buttonUrl:   { type: String, default: '#' },
		buttonIcon:  { type: String, default: '' },

		/* ── Ribbon ── */
		ribbonText:  { type: String, default: '' },
		ribbonColor: { type: String, default: '#FB6A00' },

		/* ── Colours ── */
		accentColor:     { type: String, default: '#FB6A00' },
		textColor:       { type: String, default: '#1E2031' },
		mutedColor:      { type: String, default: '#7f8c99' },
		buttonBg:        { type: String, default: '#FB6A00' },
		buttonTextColor: { type: String, default: '#FFFFFF' },
		iconBg:          { type: String, default: '' },

		highlighted:   { type: [Boolean, String], default: false },
		footerNote:    { type: String, default: '' }
	},

	computed: {
		isHighlighted: function () {
			return this.highlighted === true || this.highlighted === 'true';
		},

		parsedFeatures: function () {
			try {
				var arr = JSON.parse(this.features);
				return Array.isArray(arr) ? arr : [];
			} catch (e) { return []; }
		},

		/**
		 * Inner card: transparent, no border/padding of its own. Clips the
		 * ribbon and inherits the wrapper's radius. `highlighted` draws an
		 * inset accent ring since the outer border now belongs to the wrapper.
		 */
		cardStyle: function () {
			return {
				position: 'relative',
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				textAlign: 'center',
				borderRadius: 'inherit',
				boxShadow: this.isHighlighted ? 'inset 0 0 0 2px ' + this.accentColor : 'none',
				overflow: 'hidden',
				gap: '0.75rem'
			};
		},

		ribbonStyle: function () {
			return {
				position: 'absolute',
				top: '16px',
				right: '-28px',
				background: this.ribbonColor,
				color: '#FFF',
				fontSize: '0.78em',
				fontWeight: '700',
				padding: '4px 36px',
				transform: 'rotate(45deg)',
				letterSpacing: '0.04em',
				textTransform: 'uppercase',
				zIndex: '1'
			};
		},

		iconWrapStyle: function () {
			return {
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				width: '60px',
				height: '60px',
				borderRadius: '50%',
				background: this.iconBg || this.accentColor + '18',
				marginBottom: '0.25rem'
			};
		},

		iconStyle: function () {
			return { fontSize: '1.6rem', color: this.accentColor };
		},

		planStyle: function () {
			return { fontSize: '1.2em', fontWeight: '700', color: this.textColor };
		},

		taglineStyle: function () {
			return { fontSize: '0.9em', color: this.mutedColor };
		},

		dividerStyle: function () {
			return { width: '100%', height: '1px', background: '#DDDEE0', margin: '0.25rem 0' };
		},

		priceStyle: function () {
			return { fontSize: '2.4em', fontWeight: '800', color: this.accentColor, lineHeight: '1' };
		},

		originalPriceStyle: function () {
			return {
				fontSize: '1.1em',
				color: this.mutedColor,
				textDecoration: 'line-through',
				marginRight: '0.4rem',
				verticalAlign: 'middle'
			};
		},

		periodStyle: function () {
			return { fontSize: '1em', color: this.mutedColor, marginLeft: '2px', verticalAlign: 'middle' };
		},

		featuresStyle: function () {
			return {
				listStyle: 'none',
				padding: '0',
				margin: '0',
				width: '100%',
				textAlign: 'left',
				display: 'flex',
				flexDirection: 'column',
				gap: '0.5rem'
			};
		},

		buttonStyle: function () {
			return {
				display: 'inline-flex',
				alignItems: 'center',
				justifyContent: 'center',
				gap: '0.4rem',
				padding: '0.65rem 1.75rem',
				background: this.buttonBg,
				color: this.buttonTextColor,
				borderRadius: '6px',
				fontWeight: '700',
				fontSize: '1em',
				textDecoration: 'none',
				width: '100%',
				transition: 'opacity 0.15s'
			};
		},

		noteStyle: function () {
			return { fontSize: '0.8em', color: this.mutedColor };
		}
	},

	methods: {
		featureStyle: function (feature) {
			return { color: feature.disabled ? this.mutedColor : this.textColor };
		},

		featureIconStyle: function (feature) {
			return { color: feature.disabled ? this.mutedColor : this.accentColor };
		}
	}
};
</script>

<style scoped>
.cms-pricing-block { width: 100%; max-width: 100%; border-radius: inherit; }

.cms-pricing-block__card { width: 100%; }

.cms-pricing-block__price-wrap {
	display: flex;
	align-items: baseline;
	justify-content: center;
	gap: 0;
}

.cms-pricing-block__currency {
	font-size: 0.5em;
	vertical-align: super;
	font-weight: 700;
}

.cms-pricing-block__feature {
	display: flex;
	align-items: center;
	gap: 0.6rem;
	font-size: 1em;
}

.cms-pricing-block__feature--disabled {
	opacity: 0.55;
}

.cms-pricing-block__feature-icon { font-size: 0.9em; }

.cms-pricing-block__cta-wrap { width: 100%; }

.cms-pricing-block__cta:hover { opacity: 0.88; }
</style>
